/**
 * Local-first comment/suggestion threads of a page.
 *
 * Threads are sidecar `block` rows (see `@folio/shared` comments/model.ts):
 * this hook derives them reactively from the page's block query (already
 * subscribed by the editor), and writes through the thread mutations. Editor
 * anchoring (marks) and outcome application live in `../lib/suggestion-ops`.
 *
 * Guest-first: everything runs on local SQLite with the guest identity and
 * syncs later; on shared pages (multi-org) collaborator threads arrive through
 * the normal block sync (the server stamps authority, CC-40).
 */
import { createMemo } from "solid-js";
import type { Editor } from "@tiptap/core";
import type { Block } from "@folio/powersync";
import {
  addThreadMessage,
  blocksToThreads,
  type CommentAuthor,
  type CommentThread,
  createCommentThread,
  createCommentThreadMutation,
  createSuggestionThread,
  deleteCommentThreadMutation,
  reopenThread,
  resolveThread,
  updateCommentThreadMutation,
} from "@folio/shared";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import {
  applySuggestionOutcome,
  markedText,
  markSelectionWithComment,
  removeCommentMarks,
  selectionText,
  threadMarkName,
} from "../lib/suggestion-ops";

export interface UsePageCommentsOptions {
  pageId: () => string;
  editor: () => Editor | null;
  /** Block rows of the page (reuse the page's existing reactive query). */
  blocks: () => Block[];
}

export function usePageComments(options: UsePageCommentsOptions) {
  const drizzle = useDrizzle();
  const { userId, data } = useSession();

  const author = (): CommentAuthor => {
    const user = data?.user;
    return { id: userId(), name: user?.name?.trim() || user?.email || "Guest" };
  };

  const threads = createMemo<CommentThread[]>(() => blocksToThreads(options.blocks()));
  const openCount = createMemo(() => threads().filter((t) => t.status === "open").length);

  /** New thread (comment or suggestion): fresh ID, no row ambiguity. */
  const persistNew = async (thread: CommentThread) => {
    const db = drizzle();
    if (!db) {
      return;
    }
    await createCommentThreadMutation(db, options.pageId(), userId(), thread);
  };

  /**
   * ALREADY existing thread (reply/resolve/reopen/review). Mutation
   * verifies write and THROWS if it does not land (see
   * `packages/shared/src/powersync/verified-write.ts`).
   */
  const persistUpdate = async (thread: CommentThread) => {
    const db = drizzle();
    if (!db) {
      return;
    }
    await updateCommentThreadMutation(db, thread);
  };

  /** LIVE text the thread's mark anchors right now ("" if the mark is gone). */
  const liveAnchorText = (thread: CommentThread): string => {
    const editor = options.editor();
    if (!editor) {
      return thread.anchorText;
    }
    return markedText(editor.state.doc, threadMarkName(thread), thread.id) || thread.anchorText;
  };

  /** New comment thread on the current selection. False if nothing is selected. */
  const addComment = async (text: string): Promise<boolean> => {
    const editor = options.editor();
    const trimmed = text.trim();
    if (!editor || trimmed === "") {
      return false;
    }
    const anchorText = selectionText(editor);
    if (anchorText.trim() === "") {
      return false;
    }
    const thread = createCommentThread({ author: author(), text: trimmed, anchorText });
    if (!markSelectionWithComment(editor, thread.id)) {
      return false;
    }
    await persistNew(thread);
    return true;
  };

  const reply = async (thread: CommentThread, text: string) => {
    const next = addThreadMessage(thread, author(), text);
    if (next !== thread) {
      await persistUpdate(next);
    }
  };

  /** Resolve a comment: the anchor mark is removed, the discussion is kept. */
  const resolve = async (thread: CommentThread) => {
    const editor = options.editor();
    if (editor && thread.kind === "comment") {
      removeCommentMarks(editor, thread.id);
    }
    await persistUpdate(resolveThread(thread));
  };

  const reopen = async (thread: CommentThread) => {
    await persistUpdate(reopenThread(thread));
  };

  /** Accept/reject a suggestion: applies the outcome to the document. */
  const review = async (thread: CommentThread, accept: boolean) => {
    const editor = options.editor();
    if (!editor || thread.kind !== "suggestion") {
      return;
    }
    // Snapshot the anchored text before the marks disappear (panel context).
    const anchorText = liveAnchorText(thread);
    applySuggestionOutcome(editor, thread, accept);
    await persistUpdate(resolveThread({ ...thread, anchorText }, accept ? "accepted" : "rejected"));
  };

  /** Delete a thread entirely (marks + sidecar row). */
  const remove = async (thread: CommentThread) => {
    const editor = options.editor();
    if (editor) {
      if (thread.kind === "comment") {
        removeCommentMarks(editor, thread.id);
      } else if (thread.status === "open") {
        // Discarding an open suggestion = rejecting it silently.
        applySuggestionOutcome(editor, thread, false);
      }
    }
    const db = drizzle();
    if (db) {
      await deleteCommentThreadMutation(db, thread.id);
    }
  };

  /** Wire-up for the SuggestionMode extension callbacks. */
  const onSuggestInsert = (suggestionId: string) => {
    const thread = createSuggestionThread({
      author: author(),
      action: "insert",
      anchorText: "",
      id: suggestionId,
    });
    void persistNew(thread).catch((err: unknown) => {
      console.error("Error creating suggestion thread:", err);
    });
  };

  const onSuggestDelete = (suggestionId: string, text: string) => {
    const thread = createSuggestionThread({
      author: author(),
      action: "delete",
      anchorText: text,
      id: suggestionId,
    });
    void persistNew(thread).catch((err: unknown) => {
      console.error("Error creating suggestion thread:", err);
    });
  };

  return {
    threads,
    openCount,
    liveAnchorText,
    addComment,
    reply,
    resolve,
    reopen,
    review,
    remove,
    onSuggestInsert,
    onSuggestDelete,
  };
}
